import React, { useState, useEffect, useCallback } from 'react';
import { getCurrentSession } from '../services/authService';
import './OpsSettings.css';

const API_BASE = process.env.REACT_APP_MOVORRA_API_URL || 'http://localhost:8081';

async function getAuthHeaders(authSession) {
  let token = authSession?.idToken;
  try { const s = await getCurrentSession(); token = s.idToken; } catch (e) {}
  const h = {};
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

// Minimal ops configurability: just the two transactional-email recipients.
// Booking notifications go out when a reservation is made; accounting/invoicing
// when a trip completes. Everything else was removed by request.
function OpsSettings({ authSession }) {
  const [company, setCompany] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchCompany = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders(authSession);
      const res = await fetch(`${API_BASE}/api/companies`, { headers });
      if (res.ok) {
        const companies = await res.json();
        if (companies.length > 0) {
          setCompany(companies[0]);
          setForm(companies[0]);
        }
      }
    } finally { setLoading(false); }
  }, [authSession]);

  useEffect(() => { fetchCompany(); }, [fetchCompany]);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setSaved(false); };

  const handleSave = async () => {
    if (!company) return;
    setSaving(true);
    setSaved(false);
    try {
      const headers = await getAuthHeaders(authSession);
      // Send the full company back with the two edited fields so nothing else is wiped.
      const res = await fetch(`${API_BASE}/api/companies/${company.companyId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) { setSaved(true); fetchCompany(); }
    } finally { setSaving(false); }
  };

  if (loading) return <div className="st-root"><div className="st-loading">Loading…</div></div>;

  return (
    <div className="st-root">
      <div className="st-content" style={{ maxWidth: 640 }}>
        <div className="st-panel">
          <div className="st-panel-head">
            <div>
              <div className="st-panel-title">Configurability</div>
              <div className="st-panel-sub">Where booking and accounting emails are sent</div>
            </div>
            <button className="st-save-btn" onClick={handleSave} disabled={saving || !company}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
          <div className="st-form-grid">
            <div className="st-field st-field-full">
              <label>Booking notifications email</label>
              <input
                type="email"
                value={form.bookingNotificationEmail || ''}
                onChange={e => set('bookingNotificationEmail', e.target.value)}
                placeholder="ops@company.com"
              />
              <span className="st-field-hint">Notified when a booking reservation is made</span>
            </div>
            <div className="st-field st-field-full">
              <label>Accounting / invoicing email</label>
              <input
                type="email"
                value={form.accountingEmail || ''}
                onChange={e => set('accountingEmail', e.target.value)}
                placeholder="accounting@company.com"
              />
              <span className="st-field-hint">Notified when a trip completes, for invoicing</span>
            </div>
          </div>
          {saved && (
            <div className="st-field-hint" style={{ marginTop: 10, color: '#16a34a' }}>Saved.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OpsSettings;
